import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  trainingEnabled: boolean;
  isTraining?: boolean;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
  onComplete: (isTraining: boolean) => void | Promise<void>;
}

// Same two-second gesture as takeaway, with cancellation and click suppression
// so releasing a long press cannot also trigger a normal fiscal completion.
export default function TrainingCompleteButton({
  trainingEnabled, isTraining = false, disabled = false, className = '', children, onComplete
}: Props) {
  const { t } = useTranslation('orders');
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submittingTraining, setSubmittingTraining] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);
  const activePointer = useRef<number | null>(null);
  const suppressClick = useRef(false);
  const inFlight = useRef(false);

  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    held.current = false;
    activePointer.current = null;
    setArmed(false);
  };

  useEffect(() => {
    const onBlur = () => { suppressClick.current = true; cancel(); };
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('blur', onBlur);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  useEffect(() => {
    // A setting/status change while the finger is down cancels the gesture.
    if (activePointer.current !== null) suppressClick.current = true;
    cancel();
  }, [trainingEnabled, disabled, isTraining]);

  const complete = async (training: boolean) => {
    if (disabled || inFlight.current) return;
    inFlight.current = true;
    setSubmittingTraining(training);
    setBusy(true);
    try { await onComplete(training); }
    finally { inFlight.current = false; setBusy(false); setSubmittingTraining(false); }
  };

  const training = isTraining || armed || submittingTraining;
  return (
    <button
      type="button"
      disabled={disabled || busy}
      title={trainingEnabled ? t('platformTraining.holdHint') : undefined}
      className={`${className} select-none touch-manipulation transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
        training ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'cashier-primary bg-blue-600 hover:bg-blue-700 text-white'
      }`}
      onContextMenu={e => e.preventDefault()}
      onPointerDown={e => {
        if (e.button !== 0 || e.isPrimary === false || disabled || inFlight.current) return;
        cancel();
        suppressClick.current = false;
        activePointer.current = e.pointerId;
        if (trainingEnabled && !isTraining) {
          timer.current = setTimeout(() => {
            held.current = true;
            setArmed(true);
          }, 2000);
        }
      }}
      onPointerUp={e => {
        if (activePointer.current !== e.pointerId) return;
        const trainingRequested = isTraining || (trainingEnabled && held.current);
        suppressClick.current = true;
        cancel();
        void complete(trainingRequested);
      }}
      onPointerLeave={() => { if (activePointer.current !== null) suppressClick.current = true; cancel(); }}
      onPointerCancel={() => { suppressClick.current = true; cancel(); }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') { suppressClick.current = false; cancel(); }
      }}
      onClick={() => {
        // Pointer-up already submitted. Keyboard/assistive clicks use the
        // ordinary action; they never accidentally inherit a cancelled hold.
        if (!suppressClick.current) void complete(isTraining);
      }}
    >
      {training ? t('platformTraining.complete') : children}
    </button>
  );
}
